import {
  canManageCadProject,
  canPublishCadProject,
  isCadProjectShared,
} from './cadAccess';

/**
 * Mis listas: lo que trae `GET /getcadprojects`.
 * - Propio (sin `owner_user_id`): owner/staff (`can_publish`) y editor de
 *   empresa (`can_manage`, sin publish).
 * - Espejo de espacio (`?owner_user_id=` ajeno): la API ya acota al owner y
 *   da 403 si no sos miembro; el invitado llega con `can_manage`/`can_publish`
 *   en false. Con `spaceMirror` no se filtra por ACL (solo `slug`); las
 *   acciones de dueño siguen gateadas por esos flags en la card/detalle.
 *
 * @param {object[]} projects
 * @param {{ spaceMirror?: boolean }} [options]
 * @returns {object[]}
 */
export function ownedCadProjectsForLists(projects, { spaceMirror = false } = {}) {
  return (Array.isArray(projects) ? projects : []).filter((project) => (
    Boolean(project?.slug)
    && (spaceMirror || canPublishCadProject(project) || canManageCadProject(project))
  ));
}

/**
 * @deprecated Usar `ownedCadProjectsForLists` + GET shared-with-me.
 * Conservado por tests de transición: owner → mine, el resto → shared.
 * @param {object[]} projects
 * @returns {{ owned: object[], shared: object[] }}
 */
export function splitCadProjectsForLists(projects) {
  const owned = [];
  const shared = [];
  (Array.isArray(projects) ? projects : []).forEach((project) => {
    if (!project?.slug) return;
    if (canPublishCadProject(project) || canManageCadProject(project)) {
      owned.push(project);
      return;
    }
    shared.push(project);
  });
  return { owned, shared };
}

function cadSearchHaystack(project) {
  return [project?.name, project?.slug, project?.company_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * @param {object[]} projects
 * @param {string} term
 */
export function filterCadProjectsBySearch(projects, term) {
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return Array.isArray(projects) ? projects : [];
  return (Array.isArray(projects) ? projects : []).filter((project) => (
    cadSearchHaystack(project).includes(needle)
  ));
}

/**
 * Chips Todos / Público / Privado del panel. Igual que listas:
 * Público/Privado solo listan colecciones shared.
 * @param {object} project
 * @param {string} filter
 */
export function matchesCadVisibilityFilter(project, filter) {
  if (filter === 'all') return true;
  if (!isCadProjectShared(project)) return false;
  const mode = project?.access_mode === 'restricted' ? 'restricted' : 'public';
  if (filter === 'public') return mode === 'public';
  if (filter === 'private') return mode === 'restricted';
  return true;
}

/**
 * @param {object[]} projects
 * @param {{ search?: string, visibilityFilter?: string }} [options]
 */
export function filterCadProjectsForLists(projects, { search = '', visibilityFilter = 'all' } = {}) {
  return filterCadProjectsBySearch(projects, search)
    .filter((project) => matchesCadVisibilityFilter(project, visibilityFilter));
}
